import { Create, useForm, useSelect } from "@refinedev/antd";
import { Form } from "antd";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "../../api";
import React from "react";

// Import our component modules
import {
    BasicInfoCard,
    PriceInfoCard,
    TaxInfoCard,
    generateItemCode,
    TaxOption
} from "./components";

export interface Tax {
    id: number;
    taxType: string;
    rate: number;
    isActive: boolean;
}

export const InventoryCreate: React.FC = () => {
    const [itemName, setItemName] = useState<string>("");

    // Use Refine's useForm hook and get the form instance
    const { formProps, saveButtonProps, form } = useForm({
        resource: "inventory",
        redirect: "list",
        successNotification: {
            message: "Inventory item created successfully",
            type: "success",
        },
    });

    // Get currencies for dropdown
    const { selectProps: currencySelectProps } = useSelect({
        resource: "currency",
        optionLabel: "currencyCode",
        optionValue: "currencyCode",
    });

    // Fetch taxes with useQuery instead of useSelect
    const { data: taxesData, isLoading: taxesLoading } = useQuery({
        queryKey: ['taxes'],
        queryFn: () => api.getTaxes(),
        onSuccess: (data) => {
            console.log("Taxes fetched successfully:", data);

            // Convert to the format needed for Select
            const options = data.map((tax: Tax) => ({
                label: `${tax.taxType} (${tax.rate}%)`,
                value: tax.id,
                data: tax
            }));

            setTaxOptions(options);
        },
        onError: (error) => {
            console.error("Error fetching taxes:", error);
        }
    });

    // Create tax options manually from the raw data
    // This bypasses the refine processing that's causing issues
    const [taxOptions, setTaxOptions] = useState<TaxOption[]>([]);

    // Handle item name change
    const handleItemNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setItemName(e.target.value);
    };

    // Generate code wrapper that uses the current item name
    const generateCodeFromName = () => {
        return generateItemCode(itemName);
    };

    // Initialize form with default values
    useEffect(() => {
        // Generate initial item code and barcode
        const initialCode = generateItemCode();
        const currentDate = dayjs();

        // Set default values for the form
        form.setFieldsValue({
            ItemCode: initialCode.itemCode,
            Barcode: initialCode.barcode,
            IsActive: true,
            Price: {
                BasePrice: 0,
                DiscountType: "None",
                DiscountValue: 0,
                MinimumQuantity: 1,
                MaximumQuantity: 999999,
                EffectivePrice: 0,
                StartDate: currentDate,
                IsActive: true,
                CurrencyCode: "USD"
            },
            Tax: {
                IsActive: true
            }
        });
    }, [form]);

    // Update item code if name changes
    useEffect(() => {
        if (itemName) {
            const generated = generateItemCode(itemName);
            // Explicitly set the form values to ensure they're visible
            form.setFieldsValue({
                ItemCode: generated.itemCode,
                Barcode: generated.barcode
            });
        }
    }, [itemName, form]);

    // Calculate effective price when base price, discount type, or discount value changes
    const updateEffectivePrice = () => {
        const basePrice = form.getFieldValue(['Price', 'BasePrice']) || 0;
        const discountType = form.getFieldValue(['Price', 'DiscountType']);
        const discountValue = form.getFieldValue(['Price', 'DiscountValue']) || 0;

        let effectivePrice = basePrice;

        if (discountType === "Percentage") {
            effectivePrice = basePrice - (basePrice * (discountValue / 100));
        } else if (discountType === "FixedAmount") {
            effectivePrice = basePrice - discountValue;
        }

        // Ensure effective price is not negative
        effectivePrice = Math.max(0, effectivePrice);

        form.setFieldsValue({
            Price: {
                ...form.getFieldValue('Price'),
                EffectivePrice: effectivePrice
            }
        });

        // Also update the DefaultUnitPrice
        form.setFieldsValue({
            DefaultUnitPrice: effectivePrice
        });
    };

    // Handle tax selection
    const handleTaxSelect = (taxId: string) => {
        console.log("Tax selected:", taxId);

        // Find the selected tax in our custom tax options
        const selectedTax = taxOptions.find(option =>
            option.value.toString() === taxId
        );

        console.log("Selected tax from options:", selectedTax);

        if (selectedTax) {
            const { taxType, rate, isActive } = selectedTax.data;

            console.log(`Setting tax data - Type: ${taxType}, Rate: ${rate}, Active: ${isActive}`);

            // Set the values in the form
            form.setFieldsValue({
                Tax: {
                    ...form.getFieldValue('Tax'),
                    TaxType: taxType,
                    Rate: rate,
                    IsActive: isActive
                }
            });
        }
    };

    return (
        <Create
            saveButtonProps={saveButtonProps}>
            <Form
                {...formProps}
                layout="vertical"
                onValuesChange={(changedValues: any) => {
                    // If price-related fields change, update effective price
                    if (changedValues.Price?.BasePrice !== undefined ||
                        changedValues.Price?.DiscountType !== undefined ||
                        changedValues.Price?.DiscountValue !== undefined) {
                        updateEffectivePrice();
                    }

                    // If item name changes, update state
                    if (changedValues.ItemName !== undefined) {
                        setItemName(changedValues.ItemName);
                    }
                }}
                initialValues={{
                    IsActive: true,
                    Price: {
                        BasePrice: 0,
                        DiscountType: "None",
                        DiscountValue: 0,
                        MinimumQuantity: 1,
                        MaximumQuantity: 999999,
                        EffectivePrice: 0,
                        StartDate: dayjs(),
                        IsActive: true,
                        CurrencyCode: "USD"
                    },
                    Tax: {
                        IsActive: true
                    }
                }}
            >
                <BasicInfoCard
                    form={form}
                    itemName={itemName}
                    handleItemNameChange={handleItemNameChange}
                    generateItemCode={generateCodeFromName}
                />

                <PriceInfoCard
                    form={form}
                    currencySelectProps={currencySelectProps}
                    updateEffectivePrice={updateEffectivePrice}
                />

                <TaxInfoCard
                    form={form}
                    taxOptions={taxOptions}
                    handleTaxSelect={handleTaxSelect}
                    isLoading={taxesLoading}
                />
            </Form>
        </Create>
    );
}